import React, {useEffect, useState} from "react";
import {Button} from "reactstrap";
import {useTranslation} from "react-i18next";
import {API_URL} from "../../../config";
import {CustomModal} from "../Modal/CustomModal";

const IMAGE_TYPES = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'];

const FileIcon = ({fileName}) => {
    const name = (fileName ?? '').toLowerCase();
    if (name.includes('.pdf')) return <i className="bx bxs-file-pdf fs-5" />;
    if (name.includes('.doc')) return <i className="bx bxs-file-doc fs-5" />;
    if (name.includes('.txt')) return <i className="bx bxs-file-txt fs-5" />;
    if (name.includes('.xls')) return <i className="bx bxs-file fs-5" />;
    return <i className="bx bx-image-alt fs-5" />;
};

const fileKind = (fileName) => {
    const name = (fileName ?? '').toLowerCase();
    if (IMAGE_TYPES.some(type => name.endsWith(type))) return 'image';
    if (name.endsWith('.pdf')) return 'pdf';
    return 'other';
};

/*
Opens a stored file in a modal, so reading a receipt does not take the case off
the screen. The button says what it does instead of naming the file, and the
formats that cannot be shown inline say so.
*/
export function FileLink({fileId, fileName}) {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [hasFailed, setHasFailed] = useState(false);

    const url = fileId ? `${API_URL}/file?id=${fileId}` : null;
    const kind = fileKind(fileName);

    /*
    The file endpoint names the file in its Content-Disposition for everything
    but images, and a browser downloads what is named instead of showing it.
    Reading the PDF into a blob of our own gives the viewer something it renders
    in place.
    */
    useEffect(() => {
        if (!isOpen || kind !== 'pdf' || !url) {
            return undefined;
        }

        let objectUrl = null;
        let isCancelled = false;

        fetch(url)
            .then(response => (response.ok ? response.blob() : Promise.reject(response.status)))
            .then(blob => {
                if (isCancelled) {
                    return;
                }
                objectUrl = URL.createObjectURL(new Blob([blob], {type: 'application/pdf'}));
                setPdfUrl(objectUrl);
            })
            .catch(() => {
                if (!isCancelled) {
                    setHasFailed(true);
                }
            });

        return () => {
            isCancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            setPdfUrl(null);
            setHasFailed(false);
        };
    }, [isOpen, kind, url]);

    if (!fileId) {
        return '-';
    }

    const unavailable = (
        <div className="ds-file-preview-empty">{t('attest.filePreviewUnavailable')}</div>
    );

    const preview = () => {
        if (kind === 'image') {
            return <img src={url} alt="" className="ds-file-preview-image" />;
        }
        if (kind === 'pdf') {
            if (hasFailed) {
                return unavailable;
            }
            if (!pdfUrl) {
                return <div className="ds-file-preview-empty">{t('button.loading')}</div>;
            }
            return <iframe src={pdfUrl} title={t('button.viewFile')} className="ds-file-preview-frame" />;
        }
        return unavailable;
    };

    /*
    The endpoint serves images inline and names the rest, so a plain link opened
    some files and downloaded others. A link's download attribute is ignored
    across origins, so the bytes are read first and saved from a blob of our
    own, which makes every format behave the same.
    */
    const downloadFile = async () => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(String(response.status));
            }
            const objectUrl = URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = fileName || 'underlag';
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        } catch {
            window.open(url, '_blank', 'noopener');
        }
    };

    const footer = (
        <>
            <Button className="box-custom-grey border-0" onClick={downloadFile}>
                {t('button.downloadFile')}
            </Button>
            <Button className="box-green border-0" onClick={() => setIsOpen(false)}>
                {t('button.close')}
            </Button>
        </>
    );

    return (
        <>
            <button
                type="button"
                className="ds-file-link"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(true);
                }}
            >
                <FileIcon fileName={fileName} />
                <span className="ds-file-link-name">{t('button.viewFile')}</span>
            </button>

            {isOpen ?
                <CustomModal
                    isOpen={isOpen}
                    toggle={() => setIsOpen(prev => !prev)}
                    size="xl"
                    body={preview()}
                    footer={footer}
                />
            : ''}
        </>
    );
}
